import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs, orderBy, doc, writeBatch } from "firebase/firestore";
import Layout from "../components/Layout";
import { getISTDateString } from "../services/cashierService";
import { FaSave, FaSyncAlt, FaSearch } from "react-icons/fa";

export default function DeveloperAudit() {
  const [selectedDate, setSelectedDate] = useState(getISTDateString());
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchAuditData();
  }, [selectedDate]);

  const fetchAuditData = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "orders"),
        where("approvedDateOnly", "==", selectedDate),
        orderBy("approvedAt", "desc")
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(list);
    } catch (err) {
      console.error("Audit Fetch Error:", err);
    }
    setLoading(false);
  };

  const updateLocalOrder = (id, field, value) => {
    setOrders(prev => prev.map(o => o.id === id ? { ...o, [field]: value } : o));
  };

  const handleSave = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    if (!window.confirm(`Update order ${orderId} to [${order.payment_mode}] and [Synced:${order.ledgerSynced}]?`)) return;
    
    setSavingId(orderId);
    try {
      const batch = writeBatch(db);
      
      const rootRef = doc(db, "orders", orderId);
      batch.update(rootRef, {
        payment_mode: order.payment_mode,
        ledgerSynced: order.ledgerSynced,
        updatedBy: "DevAudit_FullEdit"
      });

      const qOutlets = query(collection(db, "outletOrders"), where("rootOrderId", "==", orderId));
      const outletSnap = await getDocs(qOutlets);
      outletSnap.forEach(d => {
        batch.update(doc(db, "outletOrders", d.id), {
          payment_mode: order.payment_mode,
          ledgerSynced: order.ledgerSynced
        });
      });

      await batch.commit();
      alert("Updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to update: " + err.message);
    }
    setSavingId(null);
  };

  const filteredOrders = orders.filter(o => {
    const s = searchTerm.toLowerCase();
    return (
      (o.displayOrderId || "").toLowerCase().includes(s) ||
      (o.partyId || "").toLowerCase().includes(s) ||
      (o.partyName || "").toLowerCase().includes(s) ||
      (o.id || "").toLowerCase().includes(s) ||
      (o.vehicleNo || "").toLowerCase().includes(s) ||
      (o.payment_mode || "").toLowerCase().includes(s) ||
      (o.status || "").toLowerCase().includes(s) ||
      String(o.ledgerSynced || false).toLowerCase().includes(s)
    );
  });

  return (
    <Layout>
      <div style={{ padding: "30px", background: "#f8fafc", minHeight: "100vh" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "30px" }}>
          <div>
             <h2 style={{ fontSize: "26px", fontWeight: 800, color: "#1e293b", margin: 0 }}>Data Control Center</h2>
             <p style={{ color: "#64748b", fontSize: "14px", marginTop: "4px" }}>Audit every financial field and order status.</p>
          </div>
          
          <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
            <div style={{ position: "relative" }}>
               <FaSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
               <input 
                 type="text" 
                 placeholder="Search all fields (Cash, True, Approved...)" 
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 style={{ 
                    padding: "10px 10px 10px 40px", 
                    borderRadius: "12px", 
                    border: "1px solid #e2e8f0", 
                    width: "300px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                    outline: "none"
                 }}
               />
            </div>

            <div style={{ display: "flex", gap: "10px", alignItems: "center", background: "#fff", padding: "8px 16px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <input 
                type="date" 
                value={selectedDate} 
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ border: "none", outline: "none", color: "#475569", fontWeight: 600, fontSize: "14px" }}
              />
              <button 
                onClick={fetchAuditData} 
                style={{ background: "transparent", color: "#3182ce", border: "none", cursor: "pointer", fontSize: "18px", display: "flex", alignItems: "center" }}
              >
                 <FaSyncAlt />
              </button>
            </div>
          </div>
        </div>

        {!loading || savingId ? (
          <div style={{ background: "#fff", borderRadius: "16px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", overflow: "hidden", border: "1px solid #edf2f7" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", color: "#64748b", textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.05em", borderBottom: "1px solid #edf2f7" }}>
                  <th style={{ padding: "15px 20px" }}>Order ID</th>
                  <th style={{ padding: "15px 20px" }}>Party ID / Name</th>
                  <th style={{ padding: "15px 20px" }}>Order Status</th>
                  <th style={{ padding: "15px 20px" }}>Payment Mode</th>
                  <th style={{ padding: "15px 20px" }}>ledgerSynced</th>
                  <th style={{ padding: "15px 20px" }}>Amount</th>
                  <th style={{ padding: "15px 20px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((o) => (
                  <tr key={o.id} style={{ borderBottom: "1px solid #f8fafc", transition: "all 0.2s" }} onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}>
                    <td style={{ padding: "15px 20px", fontWeight: 700, color: "#334155" }}>
                      {o.displayOrderId || o.orderNo || o.id}
                      <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "2px" }}>{o.id}</div>
                    </td>
                    <td style={{ padding: "15px 20px" }}>
                      <div style={{ fontWeight: 800, color: "#1e293b" }}>{o.partyId}</div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>{o.partyName}</div>
                    </td>
                    
                    <td style={{ padding: "15px 20px" }}>
                      <span style={{ 
                         padding: "4px 8px", 
                         borderRadius: "6px", 
                         fontSize: "11px", 
                         fontWeight: 800, 
                         background: o.status === "APPROVED" ? "#f0fff4" : "#fff5f5",
                         color: o.status === "APPROVED" ? "#10b981" : "#f43f5e",
                         border: `1px solid ${o.status === "APPROVED" ? "#bbf7d0" : "#fecaca"}`
                      }}>
                        {o.status || "N/A"}
                      </span>
                    </td>

                    <td style={{ padding: "15px 20px" }}>
                      <select 
                        value={o.payment_mode || "Cash"} 
                        onChange={(e) => updateLocalOrder(o.id, "payment_mode", e.target.value)}
                        style={{ padding: "8px", borderRadius: "8px", border: "1px solid #cbd5e0", fontSize: "12px", fontWeight: 700, color: "#1e293b", background: "#fff" }}
                      >
                        <option value="Cash">Cash</option>
                        <option value="Credit">Credit</option>
                        <option value="Hawker">Hawker</option>
                      </select>
                    </td>

                    <td style={{ padding: "15px 20px" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 800, fontSize: "13px", color: o.ledgerSynced ? "#10b981" : "#f59e0b" }}>
                        <input 
                          type="checkbox" 
                          checked={o.ledgerSynced || false} 
                          onChange={(e) => updateLocalOrder(o.id, "ledgerSynced", e.target.checked)} 
                        />
                        {String(o.ledgerSynced || false)}
                      </label>
                    </td>

                    <td style={{ padding: "15px 20px", fontWeight: 800, color: "#1e293b" }}>
                      ₹{Number(o.totalAmount || 0).toLocaleString()}
                    </td>

                    <td style={{ padding: "15px 20px" }}>
                       <button 
                        onClick={() => handleSave(o.id)} 
                        disabled={savingId === o.id}
                        style={{ 
                          background: "#1e293b", 
                          color: "white", 
                          border: "none", 
                          padding: "10px 18px", 
                          borderRadius: "10px", 
                          cursor: "pointer", 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "8px",
                          fontWeight: 700,
                          fontSize: "12px",
                          transition: "all 0.2s"
                        }}
                      >
                        {savingId === o.id ? "Saving..." : <><FaSave /> Save</>}
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && !loading && (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "80px", color: "#94a3b8" }}>
                      No results found for your search criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "100px", color: "#3182ce" }}>
            <p>Gathering Audit Intelligence...</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
